import { clsx } from 'clsx';

export interface BadgeProps {
  variant?: 'success' | 'warning' | 'error' | 'info' | 'gray' | 'primary';
  size?: 'sm' | 'md';
  children: React.ReactNode;
  className?: string;
  dot?: boolean;
}

export function Badge({
  variant = 'gray',
  size = 'md',
  children,
  className,
  dot = false,
}: BadgeProps) {
  const variants = {
    success: 'bg-success-50 text-success-700',
    warning: 'bg-warning-50 text-warning-700',
    error: 'bg-error-50 text-error-700',
    info: 'bg-info-50 text-info-700',
    gray: 'bg-gray-100 text-gray-700',
    primary: 'bg-primary-50 text-primary-700',
  };

  const dotColors = {
    success: 'bg-success',
    warning: 'bg-warning',
    error: 'bg-error',
    info: 'bg-info',
    gray: 'bg-gray-400',
    primary: 'bg-primary',
  };

  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-2.5 py-0.5 text-xs',
  };

  return (
    <span
      className={clsx(
        'inline-flex items-center font-medium rounded-full',
        variants[variant],
        sizes[size],
        className
      )}
    >
      {dot && (
        <span className={clsx('w-1.5 h-1.5 rounded-full mr-1.5', dotColors[variant])} />
      )}
      {children}
    </span>
  );
}

// Status-specific badges
export function StatusBadge({
  status,
}: {
  status: string;
}) {
  const statusConfig: Record<string, { variant: BadgeProps['variant']; label: string }> = {
    // Payment Link statuses
    DRAFT: { variant: 'gray', label: 'Draft' },
    ACTIVE: { variant: 'success', label: 'Active' },
    PAID: { variant: 'primary', label: 'Paid' },
    PARTIALLY_PAID: { variant: 'warning', label: 'Partial' },
    EXPIRED: { variant: 'error', label: 'Expired' },
    CANCELLED: { variant: 'gray', label: 'Cancelled' },
    REFUNDED: { variant: 'info', label: 'Refunded' },

    // Transaction statuses
    PENDING: { variant: 'warning', label: 'Pending' },
    AUTHORIZED: { variant: 'success', label: 'Authorized' },
    CAPTURED: { variant: 'success', label: 'Captured' },
    SETTLED: { variant: 'primary', label: 'Settled' },
    FAILED: { variant: 'error', label: 'Failed' },
    PARTIALLY_REFUNDED: { variant: 'info', label: 'Partial Refund' },
    CHARGEBACK: { variant: 'error', label: 'Chargeback' },

    // User statuses
    INACTIVE: { variant: 'gray', label: 'Inactive' },
    SUSPENDED: { variant: 'error', label: 'Suspended' },
    PENDING_VERIFICATION: { variant: 'warning', label: 'Pending' },
  };

  const config = statusConfig[status] || { variant: 'gray', label: status };

  return <Badge variant={config.variant} dot>{config.label}</Badge>;
}

// Payment method badge
export function PaymentMethodBadge({ method }: { method: string }) {
  const methodConfig: Record<string, { label: string; icon?: string }> = {
    CARD: { label: 'Card' },
    GCASH: { label: 'GCash' },
    MAYA: { label: 'Maya' },
    BANK_TRANSFER: { label: 'Bank' },
    GRAB_PAY: { label: 'GrabPay' },
    OTHER: { label: 'Other' },
  };

  const config = methodConfig[method] || { label: method };

  return <Badge variant="info">{config.label}</Badge>;
}

// Role badge
export function RoleBadge({ role }: { role: string }) {
  const roleConfig: Record<string, { variant: BadgeProps['variant']; label: string }> = {
    ADMIN: { variant: 'primary', label: 'Admin' },
    FINANCE: { variant: 'info', label: 'Finance' },
    STAFF: { variant: 'success', label: 'Staff' },
    CLIENT: { variant: 'gray', label: 'Client' },
  };

  const config = roleConfig[role] || { variant: 'gray', label: role };

  return <Badge variant={config.variant}>{config.label}</Badge>;
}
